import { useState, useEffect } from 'react';
import { useGlobalError } from '../context/GlobalErrorContext';
import { useGameState } from '../context/GameStateContext';
import { getTokenBalance, createCheckoutSession, getUserInviteCode, getRewardedTasksUrl } from '../services/api';

/**
 * Token balance and token-earning actions for EarnTokenModal.
 *
 * Fetches the unclaimed balance whenever `isOpen` becomes true. The actions
 * report failures through triggerError and resolve to undefined; navigation
 * and URL building are left to the caller.
 *
 * @param {boolean} isOpen - whether the modal is open
 */
export const useEarnTokens = (isOpen) => {
  const { triggerError } = useGlobalError();
  const { claimTokens: claimTokensFromContext } = useGameState();
  const [unclaimedBalance, setUnclaimedBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const fetchTokenBalance = async () => {
    try {
      setIsLoading(true);
      const data = await getTokenBalance();
      const unclaimed = parseInt(data.unclaimed_balance, 10) || 0;
      setUnclaimedBalance(unclaimed > 0 ? unclaimed : null);
    } catch (error) {
      console.error('Failed to fetch token balance:', error);
      triggerError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const claimTokens = async () => {
    try {
      setIsClaiming(true);
      await claimTokensFromContext();
      setUnclaimedBalance(null);
    } catch (error) {
      console.error('Failed to claim tokens:', error);
      triggerError(error);
    } finally {
      setIsClaiming(false);
    }
  };

  /**
   * Create a Stripe checkout session.
   * isBuying stays true on success because the caller redirects.
   * @returns {Promise<string|undefined>} the checkout URL
   */
  const createCheckout = async () => {
    try {
      setIsBuying(true);
      const response = await createCheckoutSession([{ item_id: "1" }]);
      return response.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      triggerError(error);
      setIsBuying(false);
    }
    // Note: Don't reset isBuying in finally because the caller redirects
  };

  /** @returns {Promise<string|undefined>} the user's invite code */
  const fetchInviteCode = async () => {
    try {
      setIsInviting(true);
      const response = await getUserInviteCode();
      return response.invite_code;
    } catch (error) {
      console.error('Failed to get invite code:', error);
      triggerError(error);
    } finally {
      setIsInviting(false);
    }
  };

  /** @returns {Promise<string|undefined>} the offerwall URL from the backend */
  const fetchRewardedTasksUrl = async () => {
    try {
      setIsLoadingTasks(true);
      const response = await getRewardedTasksUrl();
      return response.url;
    } catch (error) {
      console.error('Failed to get rewarded tasks URL:', error);
      triggerError(error);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTokenBalance();
    }
  }, [isOpen]);

  return {
    unclaimedBalance,
    isLoading,
    isClaiming,
    isBuying,
    isInviting,
    isLoadingTasks,
    claimTokens,
    createCheckout,
    fetchInviteCode,
    fetchRewardedTasksUrl,
  };
};

export default useEarnTokens;
